import { OFFLINE_FETCHED_AT } from "@/lib/swStrategy";
import { rememberCsrfToken, OUTBOX_SYNC_TAG } from "@/lib/outboxPush";

// Where a page the service worker made up offline has no rows on the device to show.
export const OFFLINE_EMPTY_TEXT = "Нет сети, а задач на этом устройстве ещё нет — страница заполнится, когда связь вернётся.";

export function isOfflineStandIn(fetchedAt: string | undefined): boolean {
  return fetchedAt === OFFLINE_FETCHED_AT;
}

export function registerServiceWorker(onUpdateAvailable: () => void): void {
  if (!("serviceWorker" in navigator)) return;
  // localhost is a secure context, so registration in bin/dev goes through, and
  // mark_page_cacheable stamps dev responses exactly like prod. With skipWaiting +
  // clients.claim that sticks immediately and hard: stale markup after every ERB/page
  // edit, clearable only by hand via DevTools. VITE_ENABLE_SW=1 is the deliberate
  // escape hatch for testing the SW itself locally.
  if (!import.meta.env.PROD && !import.meta.env.VITE_ENABLE_SW) return;

  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "update-available") onUpdateAvailable();
  });

  const register = () => {
    void navigator.serviceWorker.register("/sw.js").catch(() => {
      // Registration fails on http:// and in private windows. The app works without a SW.
    });
  };

  // document.readyState is already "complete" if the module was imported after
  // window.load (a lazy chunk, say) — then the load event never comes and the SW would
  // never register at all.
  if (document.readyState === "complete") register();
  else window.addEventListener("load", register);
}

// The document and Inertia page caches hold the signed-in user's pages. They must be wiped on sign-out,
// otherwise the next sign-in on the same device shows someone else's tasks until the
// first revalidate.
//
// We clear the document cache from the page rather than by messaging the worker:
// `controller` can be null (first load after registration, hard reload) and the message
// silently goes nowhere — at exactly the moment the purge matters most, on sign-out.
// The caller must not block sign-out navigation on this promise — call it as
// `void purgeDocumentCache()`.
export async function purgeDocumentCache(): Promise<void> {
  if (!("caches" in window)) return;
  try {
    const names = await caches.keys();
    await Promise.all(
      names
        .filter((n) => n.startsWith("revolver-docs-") || n.startsWith("revolver-inertia-"))
        .map((n) => caches.delete(n)),
    );
  } catch {
    // Purging is hygiene, not a correctness requirement: SW invalidation works anyway.
  }
}

// Without the purge the reload is served from the document cache, i.e. the same stale
// HTML. Offline we keep it: a purged cache plus no network is a browser error page.
export async function reloadApp(): Promise<void> {
  if (navigator.onLine) await purgeDocumentCache();
  window.location.reload();
}

interface SyncRegistration {
  sync?: { register(tag: string): Promise<void> };
}

/**
 * Asks the service worker to push the outbox once the network is back, even after the tab
 * is closed. Only Chromium has Background Sync; elsewhere the page's own retries remain.
 */
export async function requestBackgroundSync(csrfToken: string): Promise<void> {
  try {
    const registration = (await navigator.serviceWorker?.getRegistration()) as SyncRegistration | undefined;
    if (!registration?.sync) return;
    await rememberCsrfToken(csrfToken);
    await registration.sync.register(OUTBOX_SYNC_TAG);
  } catch {
    // ignored
  }
}
